// ── Saved Games Service ──────────────────────────────────────────────────────
// Manages the multi-game save system under localStorage key `action_saved_games`.
// Every game a ticket is spent on gets an entry here.

const SAVED_GAMES_KEY = 'action_saved_games'
const MAX_SAVED_GAMES = 20
const SINGLE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000   // 7 days  (single / historical)
const MULTI_EXPIRY_MS  = 24 * 60 * 60 * 1000        // 24 hours (h2h / coop)

// ── UUID helper (no crypto.randomUUID dependency) ────────────────────────────
const generateUUID = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })

// ── localStorage helpers ─────────────────────────────────────────────────────
function safeGet(fallback) {
  try {
    const raw = localStorage.getItem(SAVED_GAMES_KEY)
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

function safeSet(value) {
  try {
    localStorage.setItem(SAVED_GAMES_KEY, JSON.stringify(value))
  } catch {}
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns all saved games sorted by updatedAt descending (most recent first).
 */
export function getSavedGames() {
  return safeGet([]).sort((a, b) => b.updatedAt - a.updatedAt)
}

/**
 * Returns a single saved game by id, or null.
 */
export function getSavedGameById(id) {
  if (!id) return null
  return safeGet([]).find(g => g.id === id) ?? null
}

/**
 * Creates a new saved game entry and persists it.
 * Enforces the 20-game cap: removes oldest completed/abandoned first,
 * then oldest expired, then globally oldest.
 *
 * data shape (all optional — defaults shown):
 *   type:        'single' | 'historical' | 'h2h' | 'coop'
 *   status:      'lobby' | 'picking' | 'playing'
 *   actors:      { start: actorSnap, goal: actorSnap }
 *   hops:        0
 *   chain:       []
 *   refundEligible: true
 *   ticketSpent: 1
 *   gameId:      null   (Firestore doc ID)
 *   gameCode:    null   (lobby display code)
 *   playerKey:   null   ('p1' | 'p2')
 *
 * Returns the new entry (including generated id, createdAt, updatedAt, expiresAt).
 */
export function createSavedGame(data = {}) {
  const now = Date.now()
  const isMulti = ['h2h', 'coop'].includes(data.type)
  const expiryMs = isMulti ? MULTI_EXPIRY_MS : SINGLE_EXPIRY_MS

  const entry = {
    // defaults
    type: 'single',
    status: 'playing',
    actors: { start: null, goal: null },
    hops: 0,
    chain: [],
    refundEligible: true,
    ticketSpent: 1,
    cueCardsUsed: 0,
    gameId: null,
    gameCode: null,
    playerKey: null,
    opponentName: null,
    // caller overrides
    ...data,
    // always stamp timestamps fresh
    id: generateUUID(),
    createdAt: now,
    updatedAt: now,
    expiresAt: now + expiryMs,
  }

  let games = safeGet([])

  // Enforce cap
  if (games.length >= MAX_SAVED_GAMES) {
    const byAge = [...games].sort((a, b) => a.updatedAt - b.updatedAt)
    // 1. Remove oldest completed or abandoned
    const stale = byAge.find(g => g.status === 'completed' || g.status === 'abandoned')
    if (stale) {
      games = games.filter(g => g.id !== stale.id)
    } else {
      // 2. Remove oldest expired
      const expired = byAge.find(g => g.expiresAt < now)
      if (expired) {
        games = games.filter(g => g.id !== expired.id)
      } else {
        // 3. Remove globally oldest
        games = games.filter(g => g.id !== byAge[0].id)
      }
    }
  }

  games.push(entry)
  safeSet(games)
  return entry
}

/**
 * Merges `updates` into the saved game with the given id.
 * Always refreshes updatedAt.
 * Returns the updated entry, or null if not found.
 */
export function updateSavedGame(id, updates) {
  if (!id) return null
  const games = safeGet([])
  const idx = games.findIndex(g => g.id === id)
  if (idx === -1) return null
  games[idx] = { ...games[idx], ...updates, updatedAt: Date.now() }
  safeSet(games)
  return games[idx]
}

/**
 * Removes the saved game with the given id (no-op if not found).
 */
export function removeSavedGame(id) {
  if (!id) return
  safeSet(safeGet([]).filter(g => g.id !== id))
}

/**
 * Removes all saved games whose expiresAt is in the past.
 * Call silently on app startup.
 */
export function cleanExpiredGames() {
  const now = Date.now()
  safeSet(safeGet([]).filter(g => g.expiresAt > now))
}

// ── Ticket refund ─────────────────────────────────────────────────────────────

/**
 * Refund `amount` tickets directly in localStorage.
 * Uses the same key and encoding as storage.js (`action_tickets`, JSON-stringified).
 * Returns the new ticket count so callers can sync React state.
 *
 * @param {number} amount  Tickets to add back (default: 1).
 * @returns {number}       New ticket balance.
 */
export function refundTicket(amount = 1) {
  try {
    const raw     = localStorage.getItem('action_tickets')
    const current = raw !== null ? (JSON.parse(raw) || 0) : 0
    const next    = current + amount
    localStorage.setItem('action_tickets', JSON.stringify(next))
    return next
  } catch {
    return 0
  }
}
